import React, { useState } from 'react';
import { 
  Card, Input, Button, Tree, Space, Popover, Select, 
  Checkbox, Typography, Form, Layout, theme, Divider 
} from 'antd';
import { 
  SmileOutlined, MehOutlined, PlusOutlined, 
  DeleteOutlined, QuestionCircleOutlined, MessageOutlined,
  EditOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Header, Content } = Layout;

// Sample data for message options
const messageOptions = [
  'change-in-medication-dose-yes-follow-up',
  'change-in-medication-dose-no-follow-up',
  'medication-side-effects',
  'medication-effectiveness',
  'change-in-medication-dose-yes-fallback-1',
  'change-in-medication-dose-yes-fallback-2'
];

// Sample data for function calls
const functionNameOptions = [
  'AI-ANALYSIS',
  'SCHEDULE-APPOINTMENT',
  'GET-MEDICATION-INFO',
  'RECORD-SIDE-EFFECTS',
  'null'
];

const SurveyTreeComponent = () => {
  const { token } = theme.useToken();

  // Initial tree structure
  const [treeData, setTreeData] = useState([
    {
      title: "Hello, how can I help you today?",
      key: "0",
      isBot: true,
      message: "change-in-medication-dose-yes-follow-up",
      functionCall: {
        functionName: "AI-ANALYSIS",
        prompt: "You are an AI assistant analyzing a user's response to determine their intent."
      },
      fallbackMessages: [
        "change-in-medication-dose-yes-fallback-1"
      ],
      endOfConversation: false,
      children: [],
    },
  ]);

  const [expandedKeys, setExpandedKeys] = useState(["0"]);
  const [nextNodeKey, setNextNodeKey] = useState(1);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingNodeKey, setEditingNodeKey] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Function to add a response or question node
  const addNode = (parentKey, isBot, responseValue = "") => {
    const newNode = {
      title: isBot ? "New question" : `Response: ${responseValue || ""}`,
      key: String(nextNodeKey),
      isBot: isBot,
      message: isBot ? "New question" : "",
      functionCall: { functionName: "null", prompt: "" },
      fallbackMessages: [],
      endOfConversation: false,
      responseValue: responseValue,
      children: [],
    };

    const updateTree = (nodes) =>
      nodes.map((node) =>
        node.key === parentKey
          ? { ...node, children: [...node.children, newNode] }
          : { ...node, children: updateTree(node.children) }
      );

    setTreeData(updateTree(treeData));
    setExpandedKeys([...expandedKeys, parentKey, String(nextNodeKey)]);
    setNextNodeKey(nextNodeKey + 1);
  };

  // Function to remove a node
  const removeNode = (key) => {
    const filterTree = (nodes) =>
      nodes
        .filter((node) => node.key !== key)
        .map((node) => ({ ...node, children: filterTree(node.children) }));

    setTreeData(filterTree(treeData));
    setExpandedKeys(expandedKeys.filter((k) => k !== key));
    
    // Clear selected node if it was deleted
    if (selectedNode && selectedNode.key === key) {
      setSelectedNode(null);
    }
    if (editingNodeKey === key) {
      setEditingNodeKey(null);
    }
  };

  // Function to update a node's properties
  const updateNode = (key, updatedProps) => {
    const updateNodeInTree = (nodes) =>
      nodes.map((node) =>
        node.key === key
          ? { ...node, ...updatedProps }
          : { ...node, children: updateNodeInTree(node.children) }
      );

    const newTreeData = updateNodeInTree(treeData);
    setTreeData(newTreeData);
    
    // Update selected node if it was modified
    if (selectedNode && selectedNode.key === key) {
      const findUpdatedNode = (nodes) => {
        for (const node of nodes) {
          if (node.key === key) return node;
          const found = findUpdatedNode(node.children);
          if (found) return found;
        }
        return null;
      };
      
      setSelectedNode(findUpdatedNode(newTreeData));
    }
  };

  // Function to handle node selection
  const handleSelect = (selectedKeys, info) => {
    if (selectedKeys.length > 0) {
      const findNode = (nodes, key) => {
        for (const node of nodes) {
          if (node.key === key) return node;
          const found = findNode(node.children, key);
          if (found) return found;
        }
        return null;
      };
      
      setSelectedNode(findNode(treeData, selectedKeys[0]));
    } else {
      setSelectedNode(null);
    }
  };

  // Start editing a node message
  const startEditing = (node) => {
    setEditingNodeKey(node.key);
    setEditingText(node.isBot ? node.message : node.responseValue);
  };

  // Save edits to a node
  const saveEditing = () => {
    if (editingNodeKey) {
      const findNode = (nodes, key) => {
        for (const node of nodes) {
          if (node.key === key) return node;
          const found = findNode(node.children, key);
          if (found) return found;
        }
        return null;
      };
      
      const nodeToUpdate = findNode(treeData, editingNodeKey);
      
      if (nodeToUpdate) {
        if (nodeToUpdate.isBot) {
          updateNode(editingNodeKey, { 
            message: editingText,
            title: editingText
          });
        } else {
          updateNode(editingNodeKey, { 
            responseValue: editingText,
            title: `Response: ${editingText}`
          });
        }
      }
      
      setEditingNodeKey(null);
      setEditingText("");
    }
  };

  // Popover content for adding a response or bot question
  const responsePopoverContent = (node) => {
    // If node is a response (not a bot question), only show "Add Bot Question" button
    if (!node.isBot) {
      return (
        <div style={{ padding: 10, minWidth: 200 }}>
          <Button 
            type="primary" 
            block 
            icon={<PlusOutlined />}
            onClick={() => addNode(node.key, true)}
          >
            Add Question
          </Button>
        </div>
      );
    }
    
    // For bot questions, show both response selection and add bot question
    return (
      <div style={{ padding: 10, minWidth: 200 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            placeholder="Select response"
            style={{ width: '100%' }}
            onChange={(value) => addNode(node.key, false, value)}
          >
            <Option value="yes">Yes</Option>
            <Option value="no">No</Option>
            <Option value="other">Other</Option>
          </Select>
          <label>Add Question</label>
          <Button 
            type="primary" 
            block 
            icon={<PlusOutlined />}
            onClick={() => addNode(node.key, true)}
          >
            Add Question
          </Button>
        </Space>
      </div>
    );
  };

  // Function to render tree nodes with chat bubble UI
  const renderTreeNodes = (data) =>
    data.map((item) => (
      <Tree.TreeNode
        key={item.key}
        title={
          <Popover 
            content={responsePopoverContent(item)} 
            title="Add a response type"
            trigger="contextMenu"
            placement="right"
          >
            <div
              className={`chat-bubble ${item.isBot ? "bot" : "user"}`}
              style={{
                backgroundColor: item.isBot ? '#DCF8C6' : '#FFFFFF',
                color: 'black',
                borderRadius: '20px',
                padding: '10px 15px',
                marginBottom: '5px',
                position: 'relative',
                maxWidth: '250px',
                wordWrap: 'break-word',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                border: item.isBot ? 'none' : '1px solid #ccc',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect([item.key]);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                {item.isBot ? 
                  <QuestionCircleOutlined style={{ marginRight: '5px' }} /> : 
                  <MessageOutlined style={{ marginRight: '5px' }} />
                }
                
                {editingNodeKey === item.key ? (
                  <Input 
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onPressEnter={saveEditing}
                    onBlur={saveEditing}
                    size="small"
                    autoFocus
                    style={{ width: '100%' }}
                  />
                ) : (
                  <span onClick={() => startEditing(item)} style={{ flex: 1 }}>
                    {item.isBot ? (
                      <Text strong>{item.message || "Question"}</Text>
                    ) : (
                      <Text>{item.responseValue || "Response"}</Text>
                    )}
                  </span>
                )}
              </div>

              <div>
                {!editingNodeKey && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(item);
                    }}
                    style={{ marginRight: 4 }}
                  />
                )}
                
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNode(item.key);
                  }}
                />
              </div>
            </div>
          </Popover>
        }
      >
        {item.children.length > 0 && renderTreeNodes(item.children)}
      </Tree.TreeNode>
    ));

  // Function to export the tree data as JSON
  const exportToJson = () => {
    const transformTreeToConfig = (nodes) => {
      if (!nodes || nodes.length === 0) return [];
      
      return nodes.map(node => {
        const responseOptions = node.children
          .filter(child => !child.isBot)
          .map(respNode => {
            const followUp = respNode.children.length > 0 
              ? transformTreeToConfig(respNode.children)[0] 
              : "null";
            
            return {
              "answer": respNode.responseValue || "",
              "follow-up": followUp
            };
          });
        
        return {
          "message": node.message || "",
          "response": responseOptions.length > 0 ? responseOptions : [{ "answer": "", "follow-up": "null" }],
          "function-call": node.functionCall.functionName === "null" 
            ? "null" 
            : {
                "function-name": node.functionCall.functionName,
                "prompt": node.functionCall.prompt
              },
          "fallback-messages": node.fallbackMessages || [],
          "end-of-conversation": node.endOfConversation ? "true" : "false"
        };
      });
    };
    
    const configData = transformTreeToConfig(treeData)[0];
    console.log(JSON.stringify(configData, null, 2));
    alert("JSON data has been logged to the console");
  };

  // Node edit form
  const renderNodeEditor = () => {
    if (!selectedNode) return (
      <Card title="Node Editor" style={{ marginTop: 20 }}>
        <Text type="secondary">Select a node to edit its properties</Text>
      </Card>
    );

    return (
      <Card 
        title={selectedNode.isBot ? "Edit Question" : "Edit Response"}
        style={{ marginTop: 20 }}
      >
        <Form layout="vertical">
          {selectedNode.isBot ? (
            <>
              <Form.Item label="Message">
                <Input
                  value={selectedNode.message}
                  onChange={(e) => updateNode(selectedNode.key, { 
                    message: e.target.value,
                    title: e.target.value
                  })}
                  style={{ width: '100%' }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Or select from predefined messages:
                </Text>
                <Select
                  value={selectedNode.message}
                  onChange={(value) => updateNode(selectedNode.key, { 
                    message: value,
                    title: value
                  })}
                  style={{ width: '100%', marginTop: 8 }}
                >
                  {messageOptions.map(option => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Divider>Analyse Method</Divider>
              
              <Form.Item label="Method Name">
                <Select
                  value={selectedNode.functionCall?.functionName || "null"}
                  onChange={(value) => {
                    const functionCall = value === "null" 
                      ? { functionName: "null", prompt: "" }
                      : { functionName: value, prompt: selectedNode.functionCall?.prompt || "" };
                    updateNode(selectedNode.key, { functionCall });
                  }}
                  style={{ width: '100%' }}
                >
                  {functionNameOptions.map(option => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {selectedNode.functionCall?.functionName !== "null" && (
                <Form.Item label="Prompt">
                  <TextArea
                    value={selectedNode.functionCall?.prompt || ""}
                    onChange={(e) => {
                      const functionCall = {
                        ...selectedNode.functionCall,
                        prompt: e.target.value
                      };
                      updateNode(selectedNode.key, { functionCall });
                    }}
                    rows={5}
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              )}
              
              <Divider>Additional Options</Divider>
              
              <Form.Item label="Fallback Messages">
                <Select
                  mode="multiple"
                  value={selectedNode.fallbackMessages || []}
                  onChange={(value) => updateNode(selectedNode.key, { fallbackMessages: value })}
                  style={{ width: '100%' }}
                >
                  {messageOptions.map(option => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item label="End of Conversation">
                <Select
                  value={selectedNode.endOfConversation ? "true" : "false"}
                  onChange={(value) => updateNode(selectedNode.key, { endOfConversation: value === "true" })}
                  style={{ width: '100%' }}
                >
                  <Option value="true">True</Option>
                  <Option value="false">False</Option>
                </Select>
              </Form.Item>
            </>
          ) : (
            <Form.Item label="Response Value">
              <Input
                value={selectedNode.responseValue || ""}
                onChange={(e) => {
                  updateNode(selectedNode.key, { 
                    responseValue: e.target.value,
                    title: `Response: ${e.target.value}`
                  });
                }}
                style={{ width: '100%', marginBottom: 8 }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Or select from predefined responses:
              </Text>
              <Select
                value={selectedNode.responseValue || ""}
                onChange={(value) => {
                  updateNode(selectedNode.key, { 
                    responseValue: value,
                    title: `Response: ${value}`
                  });
                }}
                style={{ width: '100%', marginTop: 8 }}
              >
                <Option value="">Select a response</Option>
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Card>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: token.colorPrimary,
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Title level={3} style={{ margin: 0, color: '#fff' }}>Survey Tree Builder</Title>
      </Header>
      
      <Content style={{ padding: '24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          <div style={{ flex: 1 }}>
            <Card title="Conversation Tree" style={{ marginBottom: 16 }}>
              <Tree
                showLine
                blockNode
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                onSelect={handleSelect}
                selectedKeys={selectedNode ? [selectedNode.key] : []}
              >
                {renderTreeNodes(treeData)}
              </Tree>
            </Card>
            
            <Button type="primary" onClick={exportToJson} block>
              Export to JSON
            </Button>
          </div>
          
          <div style={{ flex: 1 }}>
            {renderNodeEditor()}
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default SurveyTreeComponent;